import { useState, useCallback } from 'react';
import { useDashboardStore } from '../../../../stores/dashboardStore';

// ============================================================================
// useDragDrop Hook
// ============================================================================

export interface UseDragDropReturn {
  /** Currently dragged widget instance ID */
  draggedId: string | null;
  /** Index where the widget would be dropped */
  dragOverIndex: number | null;
  /** Handler for drag start */
  handleDragStart: (e: React.DragEvent, instanceId: string) => void;
  /** Handler for drag over */
  handleDragOver: (e: React.DragEvent, index: number) => void;
  /** Handler for drop */
  handleDrop: (e: React.DragEvent) => void;
  /** Handler for drag end */
  handleDragEnd: () => void;
  /** Whether a drag operation is in progress */
  isDragging: boolean;
}

/**
 * Hook for handling drag-and-drop widget and container reordering.
 * Uses native HTML5 Drag and Drop API.
 * Supports both widgets and containers in a combined layout.
 */
export function useDragDrop(): UseDragDropReturn {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const widgets = useDashboardStore((state) => state.widgets);
  const containers = useDashboardStore((state) => state.containers);
  const setDraggedWidget = useDashboardStore((state) => state.setDraggedWidget);

  const handleDragStart = useCallback(
    (e: React.DragEvent, instanceId: string) => {
      // Set drag data
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', instanceId);

      // Set dragging state
      setDraggedId(instanceId);

      // Check if it's a widget being dragged (for EmptySlot drop detection)
      const isWidget = widgets.some((w) => w.instanceId === instanceId);
      if (isWidget) {
        setDraggedWidget(instanceId);
      }

      // Optional: Create custom drag image
      const target = e.currentTarget as HTMLElement;
      if (target) {
        // Use the element itself as drag image with slight offset
        e.dataTransfer.setDragImage(target, 20, 20);
      }
    },
    [setDraggedWidget, widgets]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';

      // Only update if over a different position
      if (dragOverIndex !== index) {
        setDragOverIndex(index);
      }
    },
    [dragOverIndex]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();

      const instanceId = e.dataTransfer.getData('text/plain');
      if (!instanceId || dragOverIndex === null) {
        return;
      }

      // Create combined sorted list of items by order
      type OrderedItem =
        | { type: 'widget'; order: number; instanceId: string }
        | { type: 'container'; order: number; instanceId: string };

      const orderedItems: OrderedItem[] = [
        ...widgets.map((w) => ({ type: 'widget' as const, order: w.order, instanceId: w.instanceId })),
        ...containers.map((c) => ({ type: 'container' as const, order: c.order, instanceId: c.instanceId })),
      ].sort((a, b) => a.order - b.order);

      // Find current index of dragged item
      const fromIndex = orderedItems.findIndex((item) => item.instanceId === instanceId);
      if (fromIndex === -1) {
        // Clear drag state and return
        setDraggedId(null);
        setDragOverIndex(null);
        setDraggedWidget(null);
        return;
      }

      const draggedItem = orderedItems[fromIndex];
      const toIndex = dragOverIndex;

      // Only reorder if position changed
      if (fromIndex !== toIndex) {
        // Calculate new order values
        // We need to update the order of the dragged item to place it at toIndex
        const targetOrder = toIndex < orderedItems.length
          ? orderedItems[toIndex].order
          : (orderedItems[orderedItems.length - 1]?.order || 0) + 1;

        // Shift orders of affected items
        const { setWidgetOrder, setContainerOrder } = useDashboardStore.getState();

        if (fromIndex < toIndex) {
          // Moving forward: decrease order of items between fromIndex+1 and toIndex
          for (let i = fromIndex + 1; i <= toIndex && i < orderedItems.length; i++) {
            const item = orderedItems[i];
            if (item.type === 'widget') {
              setWidgetOrder(item.instanceId, item.order - 1);
            } else {
              setContainerOrder(item.instanceId, item.order - 1);
            }
          }
          // Set dragged item to target order
          if (draggedItem.type === 'widget') {
            setWidgetOrder(draggedItem.instanceId, targetOrder);
          } else {
            setContainerOrder(draggedItem.instanceId, targetOrder);
          }
        } else {
          // Moving backward: increase order of items between toIndex and fromIndex-1
          for (let i = toIndex; i < fromIndex && i < orderedItems.length; i++) {
            const item = orderedItems[i];
            if (item.type === 'widget') {
              setWidgetOrder(item.instanceId, item.order + 1);
            } else {
              setContainerOrder(item.instanceId, item.order + 1);
            }
          }
          // Set dragged item to target order
          if (draggedItem.type === 'widget') {
            setWidgetOrder(draggedItem.instanceId, targetOrder);
          } else {
            setContainerOrder(draggedItem.instanceId, targetOrder);
          }
        }
      }

      // Clear drag state
      setDraggedId(null);
      setDragOverIndex(null);
      setDraggedWidget(null);
    },
    [dragOverIndex, widgets, containers, setDraggedWidget]
  );

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragOverIndex(null);
    setDraggedWidget(null);
  }, [setDraggedWidget]);

  return {
    draggedId,
    dragOverIndex,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    isDragging: draggedId !== null,
  };
}

// ============================================================================
// useDragDropItem Hook (for individual widget items)
// ============================================================================

export interface UseDragDropItemReturn {
  /** Props to spread on draggable element */
  dragProps: {
    draggable: boolean;
    onDragStart: (e: React.DragEvent) => void;
    onDragEnd: () => void;
  };
  /** Props to spread on drop target element */
  dropProps: {
    onDragOver: (e: React.DragEvent) => void;
    onDrop: (e: React.DragEvent) => void;
    onDragLeave: () => void;
  };
  /** Whether this item is being dragged */
  isDragging: boolean;
  /** Whether this item has something dragged over it */
  isDragOver: boolean;
}

/**
 * Hook for individual widget drag-drop handling.
 */
export function useDragDropItem(
  instanceId: string,
  index: number,
  isEditMode: boolean,
  dragDrop: UseDragDropReturn
): UseDragDropItemReturn {
  const { draggedId, dragOverIndex, handleDragStart, handleDragOver, handleDrop, handleDragEnd } =
    dragDrop;

  const [localDragOver, setLocalDragOver] = useState(false);

  const isDragging = draggedId === instanceId;
  const isDragOver = dragOverIndex === index && draggedId !== instanceId;

  const dragProps = {
    draggable: isEditMode,
    onDragStart: (e: React.DragEvent) => handleDragStart(e, instanceId),
    onDragEnd: handleDragEnd,
  };

  const dropProps = {
    onDragOver: (e: React.DragEvent) => {
      handleDragOver(e, index);
      setLocalDragOver(true);
    },
    onDrop: (e: React.DragEvent) => {
      handleDrop(e);
      setLocalDragOver(false);
    },
    onDragLeave: () => {
      setLocalDragOver(false);
    },
  };

  return {
    dragProps,
    dropProps,
    isDragging,
    isDragOver: isDragOver || localDragOver,
  };
}

export default useDragDrop;
